/**
 * Order Samples Drawer — add-to-cart handler
 *
 * Attaches to every `[data-samples-form]` form on the page (typically only one).
 * On submit it POSTs checked variant IDs to /cart/add.js (with `sections` like PDP ATC
 * for sync cart morph), plays the ATC checkmark burst, then dispatches `CartAddEvent`.
 */
import { CartAddEvent } from '@theme/events';
import { yieldToMainThread } from '@theme/utilities';

/**
 * Match PDP ATC timing: checkmark transitions (~0.7s) + short readable hold.
 * Do not use onAnimationEnd — CSS transitions are unreliable via getAnimations() here.
 */
const SUCCESS_ANIMATION_MS = 1000;

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];

  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  }

  return ids;
}

/**
 * @returns {Promise<{ item_count: number } & Record<string, unknown>>}
 */
async function fetchCart() {
  const cartResponse = await fetch('/cart.js');
  if (!cartResponse.ok) throw new Error('Failed to fetch cart');
  return cartResponse.json();
}

/**
 * @param {HTMLFormElement} form
 */
function initSamplesDrawer(form) {
  const dialogEl = /** @type {import('./dialog.js').DialogComponent | null} */ (
    form.closest('dialog-component')
  );
  const submitBtn = /** @type {HTMLButtonElement | null} */ (
    form.querySelector('[data-add-samples-btn]')
  );
  const picksEl = form.querySelector('[data-samples-picks]');
  const filledTpl = form.querySelector('[data-samples-pick-template]');
  const removeLabel = form.dataset.samplesRemoveLabel || '';

  /** @type {boolean} */
  let inFlight = false;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    handleSubmit();
  });

  form.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-samples-checkbox')) {
      return;
    }

    syncPicks();
  });

  form.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const removeBtn = event.target.closest('[data-samples-pick-remove]');
    if (!(removeBtn instanceof HTMLElement) || !form.contains(removeBtn)) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = removeBtn.dataset.variantId;
    if (!variantId) return;

    const checkbox = form.querySelector(
      `[data-samples-checkbox][data-variant-id="${CSS.escape(variantId)}"]`
    );
    if (!(checkbox instanceof HTMLInputElement) || checkbox.disabled) return;

    checkbox.checked = false;
    syncPicks();
    checkbox.focus();
  });

  syncPicks();

  async function handleSubmit() {
    if (!submitBtn || inFlight) return;

    const checked = /** @type {NodeListOf<HTMLInputElement>} */ (
      form.querySelectorAll('[data-samples-checkbox]:checked')
    );

    if (!checked.length) return;

    const items = Array.from(checked).map((checkbox) => ({
      id: Number(checkbox.value),
      quantity: 1,
    }));

    inFlight = true;
    setBusy(true);

    // Optimistic checkmark — same moment as PDP ATC (on click, not after fetch).
    const animationPromise = animateSuccess(submitBtn);

    try {
      const sectionIds = getCartSectionIds();
      const addUrl = Theme.routes?.cart_add_url || '/cart/add.js';

      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!addResponse.ok) {
        const errData = await addResponse.json().catch(() => ({}));
        throw new Error(errData.description || errData.message || `HTTP ${addResponse.status}`);
      }

      const addData = await addResponse.json().catch(() => ({}));

      if (typeof window.samplesHandleAddToCart === 'function') {
        window.samplesHandleAddToCart({
          response: addData,
          checkboxes: Array.from(checked),
        });
      }

      // Absolute item_count for cart-icon (non product-form sources set, not increment).
      // Keep animation and cart fetch independent so a cart.js blip does not skip morph when add already succeeded.
      const cartPromise = fetchCart().catch(() => fetchCart());

      const [, cartData] = await Promise.all([animationPromise, cartPromise]);

      for (const checkbox of checked) {
        checkbox.checked = false;
      }

      syncPicks();

      // Close samples before cart auto-open — avoids stacked modals stalling closeDialog().
      if (dialogEl && typeof dialogEl.closeDialog === 'function') {
        await dialogEl.closeDialog();
      }

      document.dispatchEvent(
        new CartAddEvent(cartData, form.id, {
          source: 'dev-samples-drawer',
          itemCount: cartData.item_count,
          sections: addData.sections,
        })
      );
    } catch (err) {
      console.error('[dev-samples-drawer] Cart add error:', err);
    } finally {
      inFlight = false;
      setBusy(false);
      // Always reset — drawer stays in DOM; stuck data-added would show checkmark on next open.
      submitBtn.removeAttribute('data-added');
    }
  }

  /**
   * Lock the button without `:disabled` / `aria-disabled` — both trigger
   * `.button[aria-disabled], .button:disabled { opacity: 0.5 }` in base.css,
   * which turns the control into an empty-looking gray slab over the checkmark.
   * @param {boolean} busy
   */
  function setBusy(busy) {
    if (!submitBtn) return;
    submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    submitBtn.classList.toggle('is-busy', busy);
  }

  function syncPicks() {
    if (!picksEl || !(filledTpl instanceof HTMLTemplateElement)) {
      return;
    }

    const checked = form.querySelectorAll('[data-samples-checkbox]:checked');
    const fragment = document.createDocumentFragment();

    for (const checkbox of checked) {
      if (!(checkbox instanceof HTMLInputElement)) continue;

      const filledRoot = filledTpl.content.firstElementChild;
      if (!filledRoot) break;

      const node = /** @type {HTMLElement} */ (filledRoot.cloneNode(true));
      const img = node.querySelector('[data-samples-pick-image]');
      const src = checkbox.dataset.samplesImage || '';
      const title = checkbox.dataset.variantTitle || '';

      if (img instanceof HTMLImageElement) {
        if (src) {
          img.src = src;
          img.alt = title;
        } else {
          img.remove();
        }
      }

      const removeBtn = node.querySelector('[data-samples-pick-remove]');
      if (removeBtn instanceof HTMLElement) {
        removeBtn.dataset.variantId = checkbox.value;
        removeBtn.setAttribute('aria-label', `${removeLabel} ${title}`.trim());
      }

      fragment.appendChild(node);
    }

    picksEl.replaceChildren(fragment);
  }
}

/**
 * Same success treatment as PDP `add-to-cart-component.animateAddToCart`.
 * @param {HTMLButtonElement} button
 */
async function animateSuccess(button) {
  button.dataset.added = 'true';
  await yieldToMainThread();
  await new Promise((resolve) => {
    setTimeout(resolve, SUCCESS_ANIMATION_MS);
  });
}

for (const form of document.querySelectorAll('[data-samples-form]')) {
  if (form instanceof HTMLFormElement) {
    initSamplesDrawer(form);
  }
}
